import { useFetchCrossTenantGeometricSearch } from '@/hooks/api/crossTenant/assetsGeometricSearch/useFetchCrossTenantGeometricSearch';
import { useFetchCrossTenantPartSearch } from '@/hooks/api/crossTenant/assetsPartSearch/useFetchCrossTenantPartSearch';
import { useFetchCrossTenantVisualSearch } from '@/hooks/api/crossTenant/assetsVisualSearch/useFetchCrossTenantVisualSearch';
import { useFetchTextSearch } from '@/hooks/api/crossTenant/textsearch/useFetchTextSearch';
import { useFetchCrossTenantMetadataSearch } from '@/hooks/api/crossTenant/metadataSearch/useFetchCrossTenantMetadataSearch';
import { useFetchVisualSearch } from '@/hooks/api/crossTenant/visualSearch/useFetchVisualSearch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useFetchAssetCompositeSearch } from '@/hooks/api/assets/assetCompositeSearch/useFetchAssetCompositeSearch';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useFileUpload } from '@/services/fileUpload';
import { useTenants } from '@/services/tenants/useTenants';
import { useViewMode } from '@/hooks/useViewMode';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AssetsPageContent } from '@/pages/AssetsPage/AssetsPage';
import { Box, styled } from '@mui/material';
import { ViewModeToggle } from '../ViewModeToggle';
import { SearchTable } from './SearchTable';
import {
  getExtensionsQueryParameter,
  getFoldersQueryParameter,
  getLabelsQueryParameter,
  getMetadataFiltersQueryParameter,
} from './utils';
import { useAssetUploadContext } from '@/services/assetUpload/useAssetUploadContext';
import { FileInputs } from '../Upload/FileInputs/FileInputs';
import { UploadDrawer } from '../Upload/UploadDrawer/UploadDrawer';

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: '400px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const emptySearchResult = async () => ({
  matches: [],
  pageData: {
    total: 0,
  },
});

const SearchResults = ({ isLoading }: { isLoading: boolean }) => {
  const [viewMode, setViewMode] = useViewMode('global');
  const { searchQuery, selectedFilters, assetId, searchType, minThreshold, sizeThreshold, getSearchTitle } =
    useSearchFilters();
  usePageTitle(getSearchTitle());

  const { fileUpload } = useFileUpload();
  const { selectedSearchTab, shouldShowSearchTabs } = useSearchTabs();
  const metadataFilters: string = getMetadataFiltersQueryParameter(selectedFilters);
  const folders: string = getFoldersQueryParameter(selectedFilters);
  const extensions: string = getExtensionsQueryParameter(selectedFilters);
  const labels: string = getLabelsQueryParameter(selectedFilters);
  const {
    fileInputProps,
    fileInputReference,
    folderInputReference,
    files,
    uploadProgress,
    drawerOpen,
    handleDrawerClose,
  } = useAssetUploadContext();
  const { isMetadataSearchEnabled, isSuitableSubstitutesEnabled } = useFeatureFlags();
  const { getTenantId } = useTenants();
  const currentTenantId = getTenantId();
  const tenantId = selectedSearchTab?.tenantId ?? currentTenantId;

  const { fetchData: fetchTextSearch, error: textSearchError } = useFetchTextSearch({
    searchQuery: searchQuery ?? '',
    selectedFilters,
    tenantId,
  });

  const { fetchData: fetchImageSearch, error: visualSearchError } = useFetchVisualSearch({
    files: fileUpload.useCase === 'visual-search' ? fileUpload.files : undefined,
    searchQuery,
    metadataFilters,
    folders,
    extensions,
    labels,
    tenantId,
  });

  const { fetchData: fetchCrossTenantGeometricSearch, error: crossTenantGeometricSearchError } =
    useFetchCrossTenantGeometricSearch({
      assetId,
      assetTenantId: currentTenantId,
      searchTenantId: tenantId,
      searchQuery,
      selectedFilters,
      minThreshold,
      enabled: !!currentTenantId,
    });

  const { fetchData: fetchCrossTenantPartSearch, error: crossTenantPartSearchError } = useFetchCrossTenantPartSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId: tenantId,
    searchQuery,
    selectedFilters,
    minThreshold,
    enabled: !!currentTenantId,
  });

  const { fetchData: fetchCrossTenantVisualSearch, error: crossTenantVisualSearchError } =
    useFetchCrossTenantVisualSearch({
      assetId,
      assetTenantId: currentTenantId,
      searchTenantId: tenantId,
      searchQuery,
      selectedFilters,
      sizeThreshold,
      enabled: !!currentTenantId,
    });

  const { fetchData: fetchCrossTenantMetadataSearch, error: crossTenantMetadataSearchError } =
    useFetchCrossTenantMetadataSearch({
      assetId,
      assetTenantId: currentTenantId,
      searchTenantId: tenantId,
      searchQuery,
      selectedFilters,
      enabled: !!currentTenantId,
    });

  const { fetchData: fetchAssetCompositeSearch, error: assetCompositeSearchError } = useFetchAssetCompositeSearch({
    assetId,
    tenantId: currentTenantId,
    searchQuery,
    selectedFilters,
    enabled: !!currentTenantId,
  });

  let fetchSearchAssets;
  let error;

  switch (searchType) {
    case 'geometric': {
      fetchSearchAssets = fetchCrossTenantGeometricSearch;
      error = crossTenantGeometricSearchError;
      break;
    }
    case 'part': {
      fetchSearchAssets = fetchCrossTenantPartSearch;
      error = crossTenantPartSearchError;
      break;
    }
    case 'substitutes': {
      if (isSuitableSubstitutesEnabled) {
        fetchSearchAssets = fetchAssetCompositeSearch;
        error = assetCompositeSearchError;
      } else {
        fetchSearchAssets = emptySearchResult;
        error = undefined;
      }
      break;
    }
    case 'visual': {
      fetchSearchAssets = fetchCrossTenantVisualSearch;
      error = crossTenantVisualSearchError;
      break;
    }
    case 'metadata': {
      if (isMetadataSearchEnabled) {
        fetchSearchAssets = fetchCrossTenantMetadataSearch;
        error = crossTenantMetadataSearchError;
      } else {
        fetchSearchAssets = emptySearchResult;
        error = undefined;
      }
      break;
    }
    case 'text': {
      fetchSearchAssets = fetchTextSearch;
      error = textSearchError;
      break;
    }
    case 'image': {
      fetchSearchAssets = fetchImageSearch;
      error = visualSearchError;
      break;
    }
    default: {
      fetchSearchAssets = emptySearchResult;
      error = undefined;
    }
  }

  const viewModeToggle = <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />;

  return (
    <>
      <Box flex={1} minHeight={0} padding='6px 6px 6px 0'>
        <SearchTable
          fetchMatches={fetchSearchAssets}
          isLoading={isLoading}
          error={error}
          shouldShowSearchTabs={shouldShowSearchTabs}
          viewMode={viewMode}
          viewModeToggle={viewModeToggle}
        />
      </Box>
      <FileInputs
        fileInputProps={fileInputProps}
        fileInputReference={fileInputReference}
        folderInputReference={folderInputReference}
      />
      <UploadDrawer files={files} uploadProgress={uploadProgress} isOpen={drawerOpen} onClose={handleDrawerClose} />
    </>
  );
};

export const SearchContent = ({ isLoading }: { isLoading: boolean }) => {
  const { permissions } = useGetCurrentUser();
  const { isEmptySearch } = useSearchFilters();

  if (permissions.canOnlySearch && isEmptySearch) {
    return;
  }

  return <ContentArea>{isEmptySearch ? <AssetsPageContent /> : <SearchResults isLoading={isLoading} />}</ContentArea>;
};
